import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, UploadCloud, Check } from "lucide-react";
import ModalShell from "@/components/ModalShell";
import MerchModal from "@/components/MerchModal";
import { confirmDialog } from "@/components/ConfirmDialog";

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function resolve(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function AdminMerch() {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const loadItems = () => api.get("/merch").then((r) => setItems(r.data));
  const loadRequests = () => api.get("/admin/merch-requests").then((r) => setRequests(r.data));

  useEffect(() => {
    loadItems();
    loadRequests();
  }, []);

  async function removeItem(item) {
    const ok = await confirmDialog({
      title: `Delete "${item.name}"?`,
      description: "This cannot be undone.",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/merch/${item.id}`);
      toast.success("Removed");
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function updateRequestStatus(req, status) {
    try {
      const { data } = await api.patch(`/admin/merch-requests/${req.id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === req.id ? data : r)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const filteredRequests =
    statusFilter === "all" ? requests : requests.filter((r) => r.status === statusFilter);

  return (
    <section className="border border-border rounded-sm bg-surface p-6 space-y-8" data-testid="admin-merch">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Merchandise</div>
          <p className="text-xs text-muted-foreground mt-1">
            Manage the Apollo merch catalog. Partners submit requests below — approve, fulfill, or decline each one.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
          data-testid="new-merch-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New Merch
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-sm">
          No merch yet. Create the first item.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="border border-border rounded-sm overflow-hidden bg-background group"
              data-testid={`merch-row-${item.id}`}
            >
              <div className="aspect-square bg-muted relative overflow-hidden">
                {item.image_url ? (
                  <img src={resolve(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                ) : null}
                {(item.image_urls?.length || 0) > 1 && (
                  <span className="absolute bottom-2 left-2 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-black/70 text-white rounded-sm">
                    +{item.image_urls.length - 1} more
                  </span>
                )}
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={() => {
                      setEditing(item);
                      setModalOpen(true);
                    }}
                    className="p-1.5 rounded-sm bg-white text-black"
                    data-testid={`merch-edit-${item.id}`}
                    aria-label="Edit"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => removeItem(item)}
                    className="p-1.5 rounded-sm text-white"
                    style={{ background: "#E15C2C" }}
                    data-testid={`merch-delete-${item.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                {!item.active && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-black/70 text-white rounded-sm">
                    Inactive
                  </span>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold truncate">{item.name}</div>
                <div className="font-mono text-xs mt-1" style={{ color: "#FBC90C" }}>
                  {money(item.price)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Requests</div>
            <p className="text-xs text-muted-foreground mt-1">Partner requests waiting for approval and fulfillment.</p>
          </div>
          <div className="flex gap-1">
            {["all", "pending", "approved", "fulfilled", "declined"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] rounded-sm border transition ${
                  statusFilter === s
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-muted-foreground hover:border-border-strong"
                }`}
                data-testid={`merch-req-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-sm">
            No requests {statusFilter !== "all" ? `with status "${statusFilter}"` : "yet"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="merch-requests-table">
              <thead>
                <tr className="text-left border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Requester</th>
                  <th className="py-2 pr-4">Item</th>
                  <th className="py-2 pr-4">Qty</th>
                  <th className="py-2 pr-4">Total</th>
                  <th className="py-2 pr-4">Notes</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-surface-elev transition" data-testid={`merch-req-${r.id}`}>
                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="text-xs font-semibold">{r.user_name}</div>
                      <div className="text-[11px] text-muted-foreground">{r.user_email}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-xs">{r.merch_name}</td>
                    <td className="py-2.5 pr-4 text-xs">{r.quantity}</td>
                    <td className="py-2.5 pr-4 text-xs font-mono" style={{ color: "#FBC90C" }}>
                      {money((r.merch_price || 0) * r.quantity)}
                    </td>
                    <td className="py-2.5 pr-4 text-[11px] text-muted-foreground max-w-[200px] truncate">{r.notes || "—"}</td>
                    <td className="py-2.5 pr-4">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex gap-1 flex-wrap">
                        {r.status === "pending" && (
                          <>
                            <button
                              onClick={() => updateRequestStatus(r, "approved")}
                              className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm text-black"
                              style={{ background: "#FBC90C" }}
                              data-testid={`merch-req-approve-${r.id}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => updateRequestStatus(r, "declined")}
                              className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm text-white"
                              style={{ background: "#E15C2C" }}
                              data-testid={`merch-req-decline-${r.id}`}
                            >
                              Decline
                            </button>
                          </>
                        )}
                        {r.status === "approved" && (
                          <button
                            onClick={() => updateRequestStatus(r, "fulfilled")}
                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm text-white flex items-center gap-1"
                            style={{ background: "#B02065" }}
                            data-testid={`merch-req-fulfill-${r.id}`}
                          >
                            <Check className="h-3 w-3" /> Fulfilled
                          </button>
                        )}
                        {(r.status === "declined" || r.status === "fulfilled") && (
                          <button
                            onClick={() => updateRequestStatus(r, "pending")}
                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-border text-muted-foreground hover:text-foreground"
                            data-testid={`merch-req-reset-${r.id}`}
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MerchModal
        open={modalOpen}
        item={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          loadItems();
        }}
      />
    </section>
  );
}

function StatusPill({ status }) {
  const inline =
    status === "approved" ? { background: "#FBC90C", color: "#000" }
    : status === "fulfilled" ? { background: "#B02065", color: "#fff" }
    : status === "declined" ? { background: "#E15C2C", color: "#fff" }
    : {};
  return (
    <span
      className="px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] bg-surface-elev"
      style={inline}
    >
      {status}
    </span>
  );
}

